import {
  autoClose,
  createLibrary,
  createParser,
  defineComponent,
  jsonToOpenUI,
  parseExpression,
  split,
  tokenize,
  type ASTNode,
  type ElementNode,
} from "@openuidev/lang-core";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import { z } from "zod/v4";

export const RICH_REPLY_FENCE = "openui-lang cleo-v1";
/** The card language's own name, a fence Cleo also writes without the version (isRichReplyFence). */
const RICH_REPLY_LANGUAGE = "openui-lang";
export const RICH_REPLY_MAX_LENGTH = 12_000;
export const RICH_REPLY_MAX_ANSWER_LENGTH = 2_000;
const id = z
  .string()
  // These are exact data keys, not program identifiers or DOM ids. A space
  // does not grant an action or change their meaning; renderers encode ids
  // where a platform requires it. Keep controls and prototype keys refused.
  .regex(/^[a-zA-Z][a-zA-Z0-9 _-]{0,47}$/)
  .refine((value) => !Object.hasOwn(Object.prototype, value) && value !== "prototype");
const label = z.string().trim().min(1).max(160);

const Choice = defineComponent({
  name: "Choice",
  description: "One comparison option, with a stable id, visible label and short factual detail.",
  props: z.object({ id, label, detail: z.string().max(600) }).strict(),
  component: null,
});
const Field = defineComponent({
  name: "Field",
  description:
    "A labelled text or select field. Text fields have no options. Select fields have 2–8 unique options.",
  props: z
    .object({
      id,
      label,
      kind: z.enum(["text", "select"]),
      required: z.boolean(),
      options: z.array(label).max(8),
    })
    .strict(),
  component: null,
});
const Comparison = defineComponent({
  name: "Comparison",
  description: "Compare 2–4 choices. One click sends the person's choice as their reply.",
  props: z.object({ id, title: label, choices: z.array(Choice.ref).min(2).max(4) }).strict(),
  component: null,
});
const Clarification = defineComponent({
  name: "Clarification",
  description: "Collect 1–6 related answers in a form for the person's next message.",
  props: z.object({ id, title: label, fields: z.array(Field.ref).min(1).max(6) }).strict(),
  component: null,
});
const Reply = defineComponent({
  name: "Reply",
  description: "One or two small interactive reply cards, alongside an ordinary prose answer.",
  props: z
    .object({
      cards: z
        .array(z.union([Comparison.ref, Clarification.ref]))
        .min(1)
        .max(2),
    })
    .strict(),
  component: null,
});

/** One model-facing contract; host applications supply their own controls. */
export const richReplyLibrary = createLibrary({
  id: "cleo-reply-v1",
  root: "Reply",
  components: [Reply, Comparison, Clarification, Choice, Field],
});

export type RichReplyChoice = z.infer<typeof Choice.props>;
export type RichReplyField = z.infer<typeof Field.props>;
export type RichReplyCard =
  | { kind: "comparison"; id: string; title: string; choices: RichReplyChoice[] }
  | { kind: "clarification"; id: string; title: string; fields: RichReplyField[] };
export type RichReplyValues = Record<string, string>;
export type RichReplyProgram =
  | { kind: "ready"; source: string; cards: RichReplyCard[]; text: string }
  | { kind: "unavailable"; reason: string };
export type RichReplySegment =
  | { kind: "markdown"; text: string; start: number }
  | { kind: "reply"; source: string; closed: boolean; index: number; start: number };

const parser = createParser(richReplyLibrary.toJSONSchema(), "root");

function assertStatic(node: ASTNode, depth = 0): void {
  if (depth > 12) throw new Error("The reply is too deeply nested.");
  switch (node.k) {
    case "Str":
    case "Bool":
    case "Null":
    case "Ref":
      return;
    case "Arr":
      if (node.els.length > 12) throw new Error("The reply has too many items.");
      node.els.forEach((child) => assertStatic(child, depth + 1));
      return;
    case "Comp":
      if (!Object.hasOwn(richReplyLibrary.components, node.name)) {
        throw new Error("The reply uses an unavailable component or action.");
      }
      node.args.forEach((child) => assertStatic(child, depth + 1));
      return;
    default:
      throw new Error("Interactive replies cannot execute expressions, tools or actions.");
  }
}

function element(value: unknown, name: string): ElementNode {
  if (value === null || typeof value !== "object")
    throw new Error("An interactive reply item is missing.");
  const node = value as ElementNode;
  if (node.type !== "element" || node.typeName !== name || node.partial || node.hasDynamicProps) {
    throw new Error("An interactive reply item is incomplete or unsupported.");
  }
  return node;
}

function unique(values: string[]): void {
  if (new Set(values).size !== values.length)
    throw new Error("Interactive reply identifiers or choices are repeated.");
}

function readCards(root: ElementNode): RichReplyCard[] {
  // OpenUI .ref produces an ElementNode at parse time, while its Zod schema
  // validates the child's props. Unwrap only for schema validation; keep the
  // typed element names below so a same-shaped foreign component cannot pass.
  const unwrap = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(unwrap);
    if (value && typeof value === "object" && (value as ElementNode).type === "element") {
      return Object.fromEntries(
        Object.entries((value as ElementNode).props).map(([key, entry]) => [key, unwrap(entry)]),
      );
    }
    return value;
  };
  element(root, "Reply");
  Reply.props.parse(unwrap(root));
  const cards = (root.props.cards as ElementNode[]).map((item): RichReplyCard => {
    if (item.typeName === "Comparison") {
      const card = element(item, "Comparison").props;
      const choices = (card.choices as ElementNode[]).map((choice) =>
        Choice.props.parse(element(choice, "Choice").props),
      );
      unique(choices.map((choice) => choice.id));
      return { kind: "comparison", id: id.parse(card.id), title: label.parse(card.title), choices };
    }
    const card = element(item, "Clarification").props;
    const fields = (card.fields as ElementNode[]).map((field) =>
      Field.props.parse(element(field, "Field").props),
    );
    unique(fields.map((field) => field.id));
    for (const field of fields) {
      unique(field.options);
      if (
        (field.kind === "text" && field.options.length !== 0) ||
        (field.kind === "select" && field.options.length < 2)
      ) {
        throw new Error("An interactive reply field has invalid choices.");
      }
    }
    return { kind: "clarification", id: id.parse(card.id), title: label.parse(card.title), fields };
  });
  unique(cards.map((card) => card.id));
  return cards;
}

export function richReplyCardText(card: RichReplyCard): string {
  const rows =
    card.kind === "comparison"
      ? card.choices.map(
          (choice) => `- ${choice.label}${choice.detail ? `: ${choice.detail}` : ""}`,
        )
      : card.fields.map(
          (field) =>
            `- ${field.label}${field.required ? " (required)" : ""}${field.options.length ? `: ${field.options.join(" / ")}` : ""}`,
        );
  return `${card.title}\n${rows.join("\n")}`;
}

/** The small static reply language accepts declared hyphenated aliases too.
 * Resolve only exact contiguous names declared on assignment lines; subtraction
 * and undeclared names still reach the ordinary rejecting expression validator.
 * Quoted data, including card ids and labels, remains byte-for-byte unchanged. */
function normalizeReplyAliases(source: string): string {
  const strings = /"(?:\\[\s\S]|[^"\\])*"|'(?:\\[\s\S]|[^'\\])*'/g;
  const code = source.replace(strings, (value) => value.replace(/[^\r\n]/g, " "));
  const occupied = new Set(code.match(/[A-Za-z][A-Za-z0-9_]*/g) ?? []);
  const aliases = new Map<string, string>();
  for (const match of code.matchAll(
    /^[ \t]*([A-Za-z][A-Za-z0-9_]*(?:-[A-Za-z0-9_]+)+)[ \t]*=(?!=)/gm,
  )) {
    const name = match[1]!;
    if (aliases.has(name)) continue;
    let index = aliases.size;
    while (occupied.has(`cleo_reply_alias_${index}`)) index++;
    const replacement = `cleo_reply_alias_${index}`;
    occupied.add(replacement);
    aliases.set(name, replacement);
  }
  if (!aliases.size) return source;
  return source.replace(
    /"(?:\\[\s\S]|[^"\\])*"|'(?:\\[\s\S]|[^'\\])*'|[A-Za-z][A-Za-z0-9_-]*/g,
    (value) => aliases.get(value) ?? value,
  );
}

export function parseRichReply(source: string): RichReplyProgram {
  try {
    if (!source.trim() || source.length > RICH_REPLY_MAX_LENGTH)
      throw new Error("The interactive reply is empty or too large.");
    source = normalizeReplyAliases(source);
    if (autoClose(source).wasIncomplete) throw new Error("The interactive reply did not finish.");
    const tokens = tokenize(source);
    if (tokens.length > 2_000) throw new Error("The interactive reply is too complex.");
    const statements = split(tokens);
    if (!statements.length || statements.length > 48)
      throw new Error("The interactive reply has too many statements.");
    unique(statements.map((statement) => statement.id));
    const expressions = new Map<string, ASTNode>();
    for (const statement of statements) {
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(statement.id))
        throw new Error("Reactive state is unavailable in this reply.");
      const expression = parseExpression(statement.tokens);
      assertStatic(expression);
      expressions.set(statement.id, expression);
    }
    // Bound reference expansion before the library evaluates it. A small
    // source can otherwise describe a cyclic or exponentially growing tree.
    let expandedNodes = 0;
    const visit = (node: ASTNode, path: ReadonlySet<string>): void => {
      if (++expandedNodes > 500 || path.size > 12)
        throw new Error("The interactive reply is too complex.");
      if (node.k === "Ref") {
        const target = expressions.get(node.n);
        if (!target || path.has(node.n))
          throw new Error("The interactive reply contains a missing or circular reference.");
        visit(target, new Set([...path, node.n]));
      } else if (node.k === "Arr") node.els.forEach((child) => visit(child, path));
      else if (node.k === "Comp") node.args.forEach((child) => visit(child, path));
    };
    for (const [name, expression] of expressions) visit(expression, new Set([name]));
    const parsed = parser.parse(source);
    if (
      !parsed.root ||
      parsed.meta.incomplete ||
      parsed.meta.errors.length ||
      parsed.meta.unresolved.length ||
      parsed.meta.orphaned.length ||
      parsed.queryStatements.length ||
      parsed.mutationStatements.length ||
      Object.keys(parsed.stateDeclarations).length
    ) {
      throw new Error("The interactive reply contains missing or unsupported content.");
    }
    const cards = readCards(parsed.root);
    // Only the validated component tree reaches React. Stray instructions,
    // unknown syntax and executable source never reach a rendering runtime.
    const canonical = jsonToOpenUI(parsed.root, richReplyLibrary);
    return {
      kind: "ready",
      source: canonical,
      cards,
      text: cards.map(richReplyCardText).join("\n\n"),
    };
  } catch (error) {
    return {
      kind: "unavailable",
      reason:
        error instanceof z.ZodError
          ? "The interactive reply has invalid fields."
          : error instanceof Error
            ? error.message
            : "The interactive reply could not be read.",
    };
  }
}

/** A comparison card in a message, with the labels a person's message can answer it with. */
export interface RichReplyComparisonChoices {
  /** The rich reply block within the message (its segment index). */
  readonly replyIndex: number;
  readonly cardId: string;
  readonly choices: ReadonlyArray<{ readonly id: string; readonly label: string }>;
}

/**
 * The comparison cards of a message's closed, valid rich replies, in order.
 * Only a message holding Cleo's card fence (isRichReplyFence) can hold one; no other is parsed.
 * Form (clarification) cards are not listed: no message answers them alone.
 */
export function richReplyComparisonChoices(text: string): RichReplyComparisonChoices[] {
  if (!text.includes(RICH_REPLY_LANGUAGE)) return [];
  const found: RichReplyComparisonChoices[] = [];
  for (const segment of splitRichReply(text)) {
    if (segment.kind !== "reply" || !segment.closed) continue;
    const parsed = parseRichReply(segment.source);
    if (parsed.kind !== "ready") continue;
    for (const card of parsed.cards)
      if (card.kind === "comparison")
        found.push({ replyIndex: segment.index, cardId: card.id, choices: card.choices });
  }
  return found;
}

/** What card answers are read from: one message of the conversation, in order. */
export interface RichReplyAnswerMessage {
  readonly id: string;
  readonly role: string;
  readonly text: string;
  readonly streaming?: boolean | undefined;
  readonly attachments?: ReadonlyArray<unknown> | undefined;
}

/** A card the person tapped, as the device that sent the tap knows it. */
export interface RichReplyTappedCard {
  readonly assistantMessageId: string;
  readonly replyIndex: number;
  readonly cardId: string;
  /** The person's own press (a trusted click on the choice in their Desk), never a choice code sent. */
  readonly pressed?: true;
}

/**
 * The person-mark law for a pressed choice (the Orchestrator, 9 Oct 2026): a choice the person pressed on Cleo's card in
 * their own Desk counts as their own Send only when its text is short enough to be read whole on the button and is
 * byte-identical to a label of a card Cleo is still waiting on in that conversation. The label renders whole, wrapped,
 * never truncated (web RichReply.tsx ChoiceCard).
 */
export const PRESSED_CHOICE_MAX_LENGTH = 80;
/**
 * Round 1 (cc-desk and Codex, P1): what he sees must be every character sent. A label earns the mark only when it is
 * plain: letters (with their marks), digits, punctuation and symbols, single spaces between them, nothing else. No
 * control, invisible, private-use, unassigned, tag or bidi characters, no line break and no doubled or edge space,
 * which would render differently from the bytes sent.
 */
const PLAIN_LABEL = /^[\p{L}\p{N}\p{P}\p{S}](?:[\p{L}\p{M}\p{N}\p{P}\p{S}]| (?! ))*$/u;
export function pressedChoiceLabelPlain(label: string): boolean {
  return PLAIN_LABEL.test(label) && !label.endsWith(" ");
}
/** A label folded the way it can look the same (compatibility forms, case): two such labels are not told apart. */
const looksLike = (label: string) => label.normalize("NFKC").toLocaleLowerCase();

/** What a pressed choice names: the card it was pressed in. */
export interface PressedChoiceClaim {
  readonly assistantMessageId: string;
  readonly replyIndex: number;
  readonly cardId: string;
}
/**
 * True only when `text` is byte-identical to one label (unique in its card's fold) of the named card, which is one of
 * Cleo's cards still waiting for an answer at the end of `messages` (the conversation as the server holds it, oldest
 * first, the new Send not among them), and `text` is within PRESSED_CHOICE_MAX_LENGTH.
 */
export function pressedChoiceOffered(
  messages: Iterable<RichReplyAnswerMessage>,
  claim: PressedChoiceClaim,
  text: string,
): boolean {
  if (text.length === 0 || text.length > PRESSED_CHOICE_MAX_LENGTH || !pressedChoiceLabelPlain(text)) return false;
  const card = answerableRichReplyCards(messages, { unconfirmedMessageIds: new Set() }).find(
    (entry) =>
      entry.assistantMessageId === claim.assistantMessageId &&
      entry.replyIndex === claim.replyIndex &&
      entry.cardId === claim.cardId,
  );
  if (card === undefined || !card.choices.some((choice) => choice.label === text)) return false;
  // No other choice of the card that looks the same (Approve and APPROVE, a full-width form).
  const all = richReplyComparisonChoices(
    [...messages].find((message) => message.id === claim.assistantMessageId)?.text ?? "",
  ).find((entry) => entry.replyIndex === claim.replyIndex && entry.cardId === claim.cardId);
  return (all?.choices ?? []).filter((choice) => looksLike(choice.label) === looksLike(text)).length === 1;
}

/** A comparison card the conversation shows answered, and the message that answered it. */
export interface RichReplyChoiceAnswer extends RichReplyTappedCard {
  readonly choiceId: string;
  readonly answerMessageId: string;
  /** False for this device's tap still going out: hide its row, credit nothing. */
  readonly confirmed: boolean;
}

// Messages are immutable rows: one parse per message (and per text, should a row change).
const comparisonChoicesByMessage = new WeakMap<
  RichReplyAnswerMessage,
  { readonly text: string; readonly cards: RichReplyComparisonChoices[] }
>();
function messageComparisonChoices(message: RichReplyAnswerMessage): RichReplyComparisonChoices[] {
  const cached = comparisonChoicesByMessage.get(message);
  if (cached && cached.text === message.text) return cached.cards;
  const cards = richReplyComparisonChoices(message.text);
  comparisonChoicesByMessage.set(message, { text: message.text, cards });
  return cards;
}

/**
 * Which of the person's messages answer a comparison card, read from the
 * conversation alone so every device agrees and nothing more is stored (the
 * Desk's rules, #1414, with the newest-message rule both apps share).
 *
 * Only the cards of Cleo's newest message since the person's last message
 * that was not a card answer can be answered: a newer message of Cleo's ends
 * the older cards' wait. A message of the person's is an answer when its
 * text, trimmed, is the label of exactly one choice among the cards that
 * count: every card of Cleo's newest message, answered or not, and every
 * older card of the run that was still unanswered when Cleo said more. That
 * choice's card must still be waiting. The card then stops waiting and the
 * run goes on, so answering one card leaves the same message's other cards
 * open. Anything else ends the run: no card waits any longer, so an old card
 * never takes a word typed much later. A label two choices share is no
 * answer, and nor is a message this device knows was a tap on another card
 * than the one its text names.
 *
 * A Send not yet confirmed takes its card, but is returned (`confirmed:
 * false`) only when this device knows it as a tap on that very card: its row
 * may then be hidden while it goes out, and it is never credited. A typed
 * one is never returned until it is confirmed. A message with attachments is
 * never an answer (hiding it would hide them), and a card still being
 * written waits for nothing.
 */
export function deriveRichReplyChoiceAnswers(
  messages: Iterable<RichReplyAnswerMessage>,
  options: RichReplyChoiceOptions,
): RichReplyChoiceAnswer[] {
  return foldRichReplyChoices(messages, options).answers;
}

/** A comparison card one click can answer now, with the choices that can. */
export interface RichReplyAnswerableCard extends RichReplyTappedCard {
  /** The choices one click can send: those whose label no other choice that counts shares. */
  readonly choices: ReadonlyArray<{ readonly id: string; readonly label: string }>;
}

/**
 * The cards a tap can answer now: a tap on one of these choices is a message
 * deriveRichReplyChoiceAnswers credits to that card, when nothing comes
 * between. Both are read from the same fold, so a card offers a click only
 * where its answer would be credited (and its row hidden). A card left out,
 * or a choice left out of its card, is answered by typing.
 */
export function answerableRichReplyCards(
  messages: Iterable<RichReplyAnswerMessage>,
  options: RichReplyChoiceOptions,
): RichReplyAnswerableCard[] {
  const { waiting, counted } = foldRichReplyChoices(messages, options);
  const uses = new Map<string, number>();
  for (const entry of counted)
    for (const choice of entry.card.choices)
      uses.set(choice.label, (uses.get(choice.label) ?? 0) + 1);
  return waiting.map((entry) => ({
    assistantMessageId: entry.assistantMessageId,
    replyIndex: entry.card.replyIndex,
    cardId: entry.card.cardId,
    choices: entry.card.choices.filter((choice) => uses.get(choice.label) === 1),
  }));
}

/** What the person's messages are read with: what this device knows of its own Sends. */
export interface RichReplyChoiceOptions {
  /** The person's messages not yet confirmed delivered (optimistic, or in an outbox). */
  readonly unconfirmedMessageIds: ReadonlySet<string>;
  /** The card a message of this device's was a tap on, when this device sent it. */
  readonly tappedCard?: ((messageId: string) => RichReplyTappedCard | undefined) | undefined;
}

type RichReplyOffered = {
  readonly assistantMessageId: string;
  readonly card: RichReplyComparisonChoices;
};

function foldRichReplyChoices(
  messages: Iterable<RichReplyAnswerMessage>,
  options: RichReplyChoiceOptions,
): {
  readonly answers: RichReplyChoiceAnswer[];
  /** The cards still waiting for an answer at the end of the conversation. */
  readonly waiting: RichReplyOffered[];
  /** The cards whose labels count at the end of the conversation. */
  readonly counted: RichReplyOffered[];
} {
  const answers: RichReplyChoiceAnswer[] = [];
  // The cards of Cleo's newest message in this run, answered ones included.
  let newest: RichReplyOffered[] = [];
  // Of those, the ones not answered yet.
  let waiting: RichReplyOffered[] = [];
  // Cards of Cleo's older messages in this run still unanswered when she said more.
  let superseded: RichReplyOffered[] = [];
  const endRun = () => {
    newest = [];
    waiting = [];
    superseded = [];
  };
  for (const message of messages) {
    if (message.role === "assistant") {
      // A message still being written (or with nothing in it) changes nothing yet.
      if (message.streaming || message.text.trim().length === 0) continue;
      superseded = [...superseded, ...waiting];
      newest = messageComparisonChoices(message).map((card) => ({
        assistantMessageId: message.id,
        card,
      }));
      waiting = newest;
      continue;
    }
    if (message.role !== "user" || (newest.length === 0 && superseded.length === 0)) continue;
    const text = message.text.trim();
    const offering =
      (message.attachments?.length ?? 0) > 0
        ? []
        : [...newest, ...superseded].filter((entry) =>
            entry.card.choices.some((choice) => choice.label === text),
          );
    const only = offering.length === 1 && waiting.includes(offering[0]!) ? offering[0]! : null;
    const choices = only ? only.card.choices.filter((choice) => choice.label === text) : [];
    const tapped = options.tappedCard?.(message.id);
    if (
      only === null ||
      choices.length !== 1 ||
      (tapped !== undefined &&
        (tapped.assistantMessageId !== only.assistantMessageId ||
          tapped.replyIndex !== only.card.replyIndex ||
          tapped.cardId !== only.card.cardId))
    ) {
      endRun();
      continue;
    }
    waiting = waiting.filter((entry) => entry !== only);
    const confirmed = !options.unconfirmedMessageIds.has(message.id);
    // Still sending and typed: the card is taken, but nothing is hidden or claimed sent.
    if (!confirmed && tapped === undefined) continue;
    answers.push({
      assistantMessageId: only.assistantMessageId,
      replyIndex: only.card.replyIndex,
      cardId: only.card.cardId,
      choiceId: choices[0]!.id,
      answerMessageId: message.id,
      confirmed,
    });
  }
  return { answers, waiting, counted: [...newest, ...superseded] };
}

/**
 * Cleo's card fence: `openui-lang cleo-v1`, or `openui-lang` with no version (W-b9529dec: an engine
 * wrote it so on the World route, and the person was shown the program as code). Both are read by this
 * one rule on every screen; a block in any other language, or a later card version, is never a card.
 */
function isRichReplyFence(info: string): boolean {
  const words = info.trim().split(/\s+/).join(" ");
  return words === RICH_REPLY_FENCE || words === RICH_REPLY_LANGUAGE;
}

/**
 * Cleo's own words in a card program that is not a valid card: its quoted text, in order, without
 * the internal ids (a lowercase word with no space) and without the program around it. What a screen
 * shows, and what the conversation reads, instead of code.
 */
export function richReplyPlainWords(source: string): string[] {
  const words: string[] = [];
  for (const match of source
    .slice(0, RICH_REPLY_MAX_LENGTH)
    .matchAll(/"((?:\\[\s\S]|[^"\\])*)"|'((?:\\[\s\S]|[^'\\])*)'/g)) {
    let value: string;
    if (match[1] !== undefined) {
      try {
        value = JSON.parse(`"${match[1]}"`) as string;
      } catch {
        value = match[1];
      }
    } else value = match[2]!.replace(/\\([\s\S])/g, "$1");
    value = value.trim();
    if (!value || /^[a-z][a-z0-9_-]*$/.test(value) || words.includes(value)) continue;
    words.push(value);
  }
  return words;
}

/** Respect enclosing Markdown fences, including longer fences used in examples. */
export function splitRichReply(text: string): RichReplySegment[] {
  const segments: RichReplySegment[] = [];
  let markdownStart = 0;
  let offset = 0;
  let fence: {
    marker: string;
    size: number;
    rich: boolean;
    start: number;
    contentStart: number;
  } | null = null;
  let index = 0;
  for (const line of text.match(/[^\n]*\n|[^\n]+$/g) ?? []) {
    const match = /^ {0,3}(`{3,}|~{3,})([^\r\n]*)\r?\n?$/.exec(line);
    if (match) {
      const marker = match[1]![0]!;
      const size = match[1]!.length;
      const info = match[2]!.trim();
      if (fence === null) {
        fence = {
          marker,
          size,
          rich: isRichReplyFence(info),
          start: offset,
          contentStart: offset + line.length,
        };
      } else if (marker === fence.marker && size >= fence.size && info === "") {
        if (fence.rich) {
          if (fence.start > markdownStart)
            segments.push({
              kind: "markdown",
              text: text.slice(markdownStart, fence.start),
              start: markdownStart,
            });
          segments.push({
            kind: "reply",
            start: fence.start,
            source: text.slice(fence.contentStart, offset),
            closed: true,
            index: index++,
          });
          markdownStart = offset + line.length;
        }
        fence = null;
      }
    }
    offset += line.length;
  }
  if (fence?.rich) {
    if (fence.start > markdownStart)
      segments.push({
        kind: "markdown",
        text: text.slice(markdownStart, fence.start),
        start: markdownStart,
      });
    segments.push({
      kind: "reply",
      source: text.slice(fence.contentStart),
      closed: false,
      index,
      start: fence.start,
    });
    markdownStart = text.length;
  }
  if (markdownStart < text.length)
    segments.push({ kind: "markdown", text: text.slice(markdownStart), start: markdownStart });
  return segments;
}

export function richReplyReadableText(text: string): string {
  return splitRichReply(text)
    .map((segment) => {
      if (segment.kind === "markdown") return segment.text;
      const parsed = segment.closed ? parseRichReply(segment.source) : null;
      if (parsed?.kind === "ready") return `${parsed.text}\n`;
      const words = parsed ? richReplyPlainWords(segment.source) : [];
      return words.length
        ? `${words.join("\n")}\n`
        : "[Interactive reply unavailable; respond using the accompanying text.]\n";
    })
    .join("");
}

export function richReplyAnswer(
  card: RichReplyCard,
  values: RichReplyValues,
): { ok: true; text: string } | { ok: false; errors: Record<string, string> } {
  if (card.kind === "comparison") {
    const choice = card.choices.find((entry) => entry.id === values.choice);
    return choice
      ? { ok: true, text: `${card.title}\nMy choice: ${choice.label}` }
      : { ok: false, errors: { choice: "Choose an option." } };
  }
  const errors: Record<string, string> = {};
  const lines: string[] = [];
  for (const field of card.fields) {
    const value = values[field.id]?.trim() ?? "";
    if (field.required && !value) errors[field.id] = "Please answer this field.";
    else if (value.length > RICH_REPLY_MAX_ANSWER_LENGTH)
      errors[field.id] = "Please shorten this answer.";
    else if (value && field.kind === "select" && !field.options.includes(value))
      errors[field.id] = "Choose one of the listed options.";
    if (value) lines.push(`${field.label}: ${value}`);
  }
  if (!lines.length && !Object.keys(errors).length)
    errors.form = "Add an answer before preparing your reply.";
  return Object.keys(errors).length
    ? { ok: false, errors }
    : { ok: true, text: `${card.title}\n${lines.join("\n")}` };
}

/** Idempotent preparation preserves unrelated prose and every attachment. */
export function appendRichReplyToDraft(draft: string, answer: string): string {
  if (draft === answer || draft.endsWith(`\n\n${answer}`)) return draft;
  return draft.length ? `${draft}\n\n${answer}` : answer;
}

export function richReplyDraftKey(
  conversationKey: string,
  messageId: string,
  source: string,
  index: number,
): string {
  const digest = bytesToHex(sha256(new TextEncoder().encode(source)));
  return `cleo:reply-draft:v1:${JSON.stringify([conversationKey, messageId, index, digest])}`;
}

export function readRichReplyValues(raw: string | null): Record<string, RichReplyValues> {
  if (!raw || raw.length > 30_000) return {};
  try {
    return z
      .record(id, z.record(id, z.string().max(RICH_REPLY_MAX_ANSWER_LENGTH)))
      .parse(JSON.parse(raw));
  } catch {
    return {};
  }
}

export const RICH_REPLY_EXAMPLE =
  'root = Reply([comparison, details])\ncomparison = Comparison("format", "Choose a format", [Choice("brief", "Brief", "A concise overview"), Choice("detailed", "Detailed", "A fuller explanation")])\ndetails = Clarification("details", "A few details", [Field("audience", "Audience", "text", true, []), Field("tone", "Tone", "select", false, ["Formal", "Conversational"])])';

// The wording that tells Cleo how to use cards is not in code: it is the
// "Chat cards" instruction, edited on Platform Admin > Instructions and
// delivered from the authority (layer interface/interactive-replies-v1).
// This module holds only what the renderer enforces: the fence, the library
// and the parser.
